import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowLeft, 
  Edit, 
  Shield, 
  DollarSign, 
  Clock,
  AlertTriangle,
  Store,
  Users,
  CheckCircle,
  XCircle
} from 'lucide-react';
import { HelpIcon } from '../components/HelpIcon';
import { getApiUrl } from '../utils/api';

interface Service {
  id: string;
  name: string;
  description: string;
  category: string;
  subcategory?: string;
  basePrice: number;
  estimatedDuration: number;
  sessionCount?: number;
  isActive: boolean;
  onlineBookingEnabled: boolean;
  depositRequired: boolean;
  depositType?: 'FIXED' | 'PERCENTAGE';
  depositAmount?: number;
  maxAdvanceBookingDays?: number;
  createdAt: string;
  updatedAt: string;
}

interface ShopAssociation {
  id: string;
  name: string;
  city: string;
  isOffered: boolean;
  isAvailable: boolean;
}

interface PractitionerAssociation {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  isOffered: boolean;
  isAvailable: boolean;
}

const categoryLabels: Record<string, string> = {
  'TATTOO': 'Tatouage',
  'PIERCING': 'Piercing',
  'CONSULTATION': 'Consultation',
  'AFTERCARE': 'Soins',
  'REMOVAL': 'Suppression',
  'JEWELRY': 'Bijouterie'
};

export function ServiceViewPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  
  const [service, setService] = useState<Service | null>(null);
  const [shops, setShops] = useState<ShopAssociation[]>([]);
  const [practitioners, setPractitioners] = useState<PractitionerAssociation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      fetchService(id);
      fetchServiceShops(id);
      fetchServicePractitioners(id);
    }
  }, [id]);

  const fetchService = async (serviceId: string) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await fetch(getApiUrl(`/api/admin/services/${serviceId}`), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch service');
      }

      const data = await response.json();
      if (data.success) {
        setService(data.data.service);
      } else {
        setError(data.message || 'Failed to load service');
      }
    } catch (error) {
      console.error('Fetch service error:', error);
      setError('Failed to load service data');
    } finally {
      setLoading(false);
    }
  };

  const fetchServiceShops = async (serviceId: string) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl(`/api/admin/services/${serviceId}/shops`), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setShops(data.data.shops || []);
        }
      }
    } catch (error) {
      console.error('Fetch service shops error:', error);
    }
  };

  const fetchServicePractitioners = async (serviceId: string) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl(`/api/admin/services/${serviceId}/practitioners`), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setPractitioners(data.data.practitioners || []);
        }
      }
    } catch (error) {
      console.error('Fetch service practitioners error:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        <span className="ml-2 text-gray-600">Chargement du service...</span>
      </div>
    );
  }

  if (error || !service) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 text-red-600 mr-2" />
            <span className="text-red-800 font-medium">
              {error || 'Service non trouvé'}
            </span>
          </div>
        </div>
        <button
          onClick={() => navigate('/services')}
          className="mt-4 btn-secondary"
        >
          Retour à la liste des services
        </button>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/services')}
            className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors border border-gray-300 shadow-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Retour à la liste</span>
          </button>
          
          <div>
            <h1 className="text-2xl font-display font-bold text-textPrimary flex items-center space-x-2">
              <span>{service.name}</span>
              <HelpIcon 
                docPath="services#details" 
                tooltip="Documentation : Détails d'un service"
              />
            </h1>
          </div>
        </div>
        
        <button
          onClick={() => navigate(`/services/${service.id}/edit`)}
          className="btn-primary flex items-center space-x-2"
        >
          <Edit className="w-4 h-4" />
          <span>Modifier</span>
        </button>
      </div>

      {/* Service Details */}
      <div className="space-y-6">
        {/* Basic Information */}
        <div className="card">
          <div className="flex items-center space-x-3 mb-6">
            <div className="flex items-center justify-center w-10 h-10 bg-primary-100 rounded-lg">
              <Shield className="w-5 h-5 text-primary-600" />
            </div>
            <h2 className="text-xl font-semibold text-textPrimary">Informations Générales</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Nom</label>
              <p className="text-textPrimary font-medium">{service.name}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Catégorie</label>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                {categoryLabels[service.category] || service.category}
              </span>
            </div>

            {service.subcategory && (
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Sous-catégorie</label>
                <p className="text-textPrimary">{service.subcategory}</p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Statut</label>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                service.isActive 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-gray-100 text-gray-800'
              }`}>
                {service.isActive ? 'Actif' : 'Inactif'}
              </span>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-500 mb-1">Description</label>
              <p className="text-textPrimary whitespace-pre-wrap">{service.description}</p>
            </div>

          </div>
        </div>

        {/* Pricing & Duration */}
        <div className="card">
          <div className="flex items-center space-x-3 mb-6">
            <div className="flex items-center justify-center w-10 h-10 bg-green-100 rounded-lg">
              <DollarSign className="w-5 h-5 text-green-600" />
            </div>
            <h2 className="text-xl font-semibold text-textPrimary">Tarification & Durée</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Prix de base</label>
              <p className="text-textPrimary font-medium text-lg">{service.basePrice.toFixed(2)}€</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Durée estimée</label>
              <div className="flex items-center space-x-2">
                <Clock className="w-4 h-4 text-gray-400" />
                <p className="text-textPrimary font-medium">{service.estimatedDuration} minutes</p>
              </div>
            </div>

            {service.sessionCount && (
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Nombre de sessions</label>
                <p className="text-textPrimary font-medium">{service.sessionCount}</p>
              </div>
            )}
          </div>

          {service.depositRequired && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <label className="block text-sm font-medium text-gray-500 mb-2">Acompte</label>
              <div className="flex items-center space-x-4">
                <span className="text-textPrimary">
                  {service.depositType === 'PERCENTAGE' 
                    ? `${service.depositAmount}%` 
                    : `${service.depositAmount?.toFixed(2)}€`}
                </span>
                <span className="text-xs text-gray-500">
                  ({service.depositType === 'PERCENTAGE' ? 'Pourcentage' : 'Montant fixe'})
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Online Booking Configuration */}
        <div className="card">
          <div className="flex items-center space-x-3 mb-6">
            <div className="flex items-center justify-center w-10 h-10 bg-blue-100 rounded-lg">
              <Shield className="w-5 h-5 text-blue-600" />
            </div>
            <h2 className="text-xl font-semibold text-textPrimary">Configuration Réservation en Ligne</h2>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-textPrimary">Réservation en ligne activée</label>
              {service.onlineBookingEnabled ? (
                <CheckCircle className="w-5 h-5 text-green-600" />
              ) : (
                <XCircle className="w-5 h-5 text-gray-400" />
              )}
            </div>

            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-textPrimary">Acompte requis</label>
              {service.depositRequired ? (
                <CheckCircle className="w-5 h-5 text-green-600" />
              ) : (
                <XCircle className="w-5 h-5 text-gray-400" />
              )}
            </div>

            {service.maxAdvanceBookingDays && (
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  Jours max. de réservation à l'avance
                </label>
                <p className="text-textPrimary">{service.maxAdvanceBookingDays} jours</p>
              </div>
            )}
          </div>
        </div>

        {/* Associated Shops */}
        <div className="card">
          <div className="flex items-center space-x-3 mb-6">
            <div className="flex items-center justify-center w-10 h-10 bg-blue-100 rounded-lg">
              <Store className="w-5 h-5 text-blue-600" />
            </div>
            <h2 className="text-xl font-semibold text-textPrimary">Shops Associés ({shops.length})</h2>
          </div>

          {shops.length > 0 ? (
            <div className="space-y-3">
              {shops.map((shop) => (
                <div key={shop.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="font-medium text-textPrimary">{shop.name}</h3>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          shop.isOffered 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {shop.isOffered ? 'Proposé' : 'Non proposé'}
                        </span>
                        {shop.isOffered && (
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                            shop.isAvailable 
                              ? 'bg-blue-100 text-blue-800' 
                              : 'bg-orange-100 text-orange-800'
                          }`}>
                            {shop.isAvailable ? 'Disponible' : 'Indisponible'}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600">{shop.city}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Store className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <p>Aucun shop associé</p>
            </div>
          )}
        </div>

        {/* Associated Practitioners */}
        <div className="card">
          <div className="flex items-center space-x-3 mb-6">
            <div className="flex items-center justify-center w-10 h-10 bg-indigo-100 rounded-lg">
              <Users className="w-5 h-5 text-indigo-600" />
            </div>
            <h2 className="text-xl font-semibold text-textPrimary">Artistes Associés ({practitioners.length})</h2>
          </div>

          {practitioners.length > 0 ? (
            <div className="space-y-3">
              {practitioners.map((practitioner) => (
                <div key={practitioner.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="font-medium text-textPrimary">{practitioner.firstName} {practitioner.lastName}</h3>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          practitioner.isOffered 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {practitioner.isOffered ? 'Proposé' : 'Non proposé'}
                        </span>
                        {practitioner.isOffered && (
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                            practitioner.isAvailable 
                              ? 'bg-blue-100 text-blue-800' 
                              : 'bg-orange-100 text-orange-800'
                          }`}>
                            {practitioner.isAvailable ? 'Disponible' : 'Indisponible'}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600">{practitioner.email}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Users className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <p>Aucun artiste associé</p>
            </div>
          )}
        </div>

        {/* Metadata */}
        <div className="card">
          <div className="flex items-center space-x-3 mb-6">
            <h2 className="text-xl font-semibold text-textPrimary">Informations Système</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Créé le</label>
              <p className="text-textPrimary">
                {new Date(service.createdAt).toLocaleDateString('fr-FR', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Modifié le</label>
              <p className="text-textPrimary">
                {new Date(service.updatedAt).toLocaleDateString('fr-FR', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

